import { useEffect, useState } from 'react';

import { viewportStale } from './viewportShift.js';

const CLOSED = { inset: 0, height: 0 };
// Less than this is the browser's own bars moving, not a keyboard
const MIN_KEYBOARD = 80;

/**
 * The on-screen keyboard on phones: how much of the screen's bottom it covers (`inset`) and the height left above it
 * (`height`), both 0 while it's closed. Browsers lay fixed elements out as if it weren't there, so a bottom sheet
 * lifts by `inset` and fits in `height` to keep its field and its main button in view. Read from the visual viewport;
 * nothing when `active` is false or the browser has no visual viewport.
 */
export const useKeyboardInset = (active = true) => {
  const [keyboard, setKeyboard] = useState(CLOSED);

  useEffect(() => {
    const viewport = typeof window === 'undefined' ? null : window.visualViewport;
    if (!active || !viewport) {
      setKeyboard(CLOSED);
      return undefined;
    }
    const update = () => {
      const covered = window.innerHeight - viewport.height - viewport.offsetTop;
      // A pinch zoom shrinks the visual viewport too, without any keyboard
      // A keyboard height iOS kept with no keyboard up (utils/viewportShift.js) is no keyboard: nothing to lift over
      const isKeyboard = covered >= MIN_KEYBOARD && Math.abs((viewport.scale || 1) - 1) < 0.01 && !viewportStale();
      setKeyboard((previous) => {
        const next = isKeyboard ? { inset: Math.round(covered), height: Math.round(viewport.height) } : CLOSED;
        return next.inset === previous.inset && next.height === previous.height ? previous : next;
      });
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [active]);

  return keyboard;
};
